// frontend/src/club/pages/ClubFinancesPage.js

import React, { useState, useEffect } from 'react';
import { useClub } from '../contexts/ClubContext';
import { Navigate } from 'react-router-dom';
import ClubLayout from '../components/layout/ClubLayout';
import api from '../../services/api';
import './ClubFinancesPage.css';

const ClubFinancesPage = () => {
  const { club, isAuthenticated, loading } = useClub();
  const [financeData, setFinanceData] = useState({
    actualBalance: 0,
    transactions: [],
    bookings: [],
    combinedTransactions: [],
    monthlyStats: {
      revenue: 0,
      bookings: 0,
      averagePrice: 0,
      completedBookings: 0,
      cancelledBookings: 0,
      refunds: 0
    }
  });
  const [dataLoading, setDataLoading] = useState(true);

  const formatNumber = (number) => {
    return Math.round(number).toString();
  };

  useEffect(() => {
    if (isAuthenticated && club?.id) {
      loadFinanceData();
    }
  }, [isAuthenticated, club]);

  const loadFinanceData = async () => {
    try {
      setDataLoading(true);

      const [balanceData, transactionsData, bookingsData] = await Promise.allSettled([
        api.balance.getClubBalance(club.id),
        api.balance.getClubTransactions(club.id),
        api.bookings.getClubBookings(club.id)
      ]);

      const originalBalance = balanceData.status === 'fulfilled' ? balanceData.value.balance : 0;
      const transactions = transactionsData.status === 'fulfilled' ? transactionsData.value : [];
      const bookings = bookingsData.status === 'fulfilled' ? bookingsData.value : [];

      const confirmedBookings = bookings.filter((booking) =>
        ['confirmed', 'completed'].includes(booking.status)
      );

      const confirmedRevenue = confirmedBookings.reduce((sum, booking) => {
        const price = parseFloat(booking.price) || 0;
        return sum + price;
      }, 0);

      const withdrawals = transactions
        .filter((t) => t.type === 'withdrawal')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

      const actualBalance = Math.max(0, confirmedRevenue - withdrawals);

      const enrichedTransactions = transactions.map((transaction) => {
        if (
          (transaction.type === 'payment' || transaction.type === 'income') &&
          transaction.booking_id
        ) {
          const relatedBooking = bookings.find((b) => b.id === transaction.booking_id);
          if (relatedBooking) {
            return {
              ...transaction,
              description: `Оплата занятия "${relatedBooking.class_name}" клиентом ${relatedBooking.first_name} ${relatedBooking.last_name}`,
              client_name: `${relatedBooking.first_name} ${relatedBooking.last_name}`,
              class_name: relatedBooking.class_name
            };
          }
        }
        return transaction;
      });

      const cancelledBookings = bookings.filter(
        (booking) =>
          booking.status === 'cancelled_by_club' ||
          (booking.status === 'cancelled' && booking.cancelled_by === 'club')
      );

      const refundTransactions = cancelledBookings.map((booking) => ({
        id: `refund_${booking.id}`,
        type: 'refund',
        amount: -(parseFloat(booking.price) || 0),
        description: `Возврат за отмененное занятие "${booking.class_name}" клиентом ${booking.first_name} ${booking.last_name}`,
        created_at: booking.cancelled_at || booking.updated_at || booking.created_at,
        booking_id: booking.id,
        client_name: `${booking.first_name} ${booking.last_name}`,
        class_name: booking.class_name,
        cancellation_reason: booking.cancellation_reason || 'Отменено администрацией клуба'
      }));

      const combinedTransactions = [...enrichedTransactions, ...refundTransactions].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const monthlyBookings = bookings.filter((booking) => {
        const bookingDate = new Date(booking.created_at);
        return bookingDate >= monthStart;
      });

      const monthlyConfirmedBookings = monthlyBookings.filter((booking) =>
        ['confirmed', 'completed'].includes(booking.status)
      );

      const monthlyCancelledBookings = monthlyBookings.filter(
        (booking) =>
          booking.status === 'cancelled_by_club' ||
          (booking.status === 'cancelled' && booking.cancelled_by === 'club')
      );

      const monthlyRevenue = monthlyConfirmedBookings.reduce((sum, booking) => {
        const price = parseFloat(booking.price) || 0;
        return sum + price;
      }, 0);

      const monthlyRefunds = monthlyCancelledBookings.reduce((sum, booking) => {
        const price = parseFloat(booking.price) || 0;
        return sum + price;
      }, 0);

      const monthlyBookingsCount = monthlyConfirmedBookings.length;
      const averagePrice = monthlyBookingsCount > 0 ? monthlyRevenue / monthlyBookingsCount : 0;

      setFinanceData({
        actualBalance,
        transactions,
        bookings,
        combinedTransactions,
        monthlyStats: {
          revenue: monthlyRevenue,
          bookings: monthlyBookingsCount,
          averagePrice,
          completedBookings: monthlyConfirmedBookings.length,
          cancelledBookings: monthlyCancelledBookings.length,
          refunds: monthlyRefunds
        }
      });
    } catch (error) {
      console.error('Ошибка загрузки финансовых данных:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const getTransactionTypeLabel = (type) => {
    const types = {
      income: 'Доход',
      payment: 'Оплачено',
      withdrawal: 'Вывод',
      refund: 'Возврат'
    };
    return types[type] || type;
  };

  const getTransactionTypeClass = (type) => {
    const classes = {
      income: 'transaction-income',
      payment: 'transaction-income',
      withdrawal: 'transaction-withdrawal',
      refund: 'transaction-refund'
    };
    return classes[type] || '';
  };

  const formatTransactionAmount = (transaction) => {
    const amount = Math.abs(transaction.amount);
    if (transaction.type === 'income' || transaction.type === 'payment') {
      return `+${formatNumber(amount)}₽`;
    }
    return `-${formatNumber(amount)}₽`;
  };

  const getTransactionDescription = (transaction) => {
    if (transaction.type === 'refund' && transaction.cancellation_reason) {
      return (
        <div>
          <div className="transaction-description-main">{transaction.description}</div>
          <div className="transaction-description-sub">
            Причина отмены: {transaction.cancellation_reason}
          </div>
        </div>
      );
    }

    return transaction.description || 'Операция';
  };

  if (loading) {
    return (
      <div className="club-loading">
        <div className="loading-spinner" />
        <p>Загрузка...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/club/login" replace />;
  }

  const netMonthIncome =
    financeData.monthlyStats.revenue - financeData.monthlyStats.refunds;

  return (
    <ClubLayout>
      <div className="club-finances-page">
        <div className="page-header">
          <div className="page-header-main">
            <div>
              <h1>Финансы клуба</h1>
              <p>Сводка по доходам и операциям за текущий месяц</p>
            </div>
          </div>
          <div className="page-header-balance">
            <span className="balance-label">Текущий баланс</span>
            <span className="balance-value">{formatNumber(financeData.actualBalance)}₽</span>
          </div>
        </div>

        {dataLoading ? (
          <div className="loading-placeholder">
            <div className="loading-spinner" />
            <p>Загрузка финансовых данных...</p>
          </div>
        ) : (
          <div className="finances-content">
            <div className="finance-stats">
              <div className="monthly-stats-expanded">
                <div className="panel-header">
                  <h3>Текущий месяц</h3>
                  <span className="panel-caption">Обобщенные показатели по бронированиям</span>
                </div>
                <div className="stats-grid-expanded">
                  <div className="stat-item-large revenue">
                    <div className="stat-content">
                      <span className="stat-label">Начисленный доход</span>
                      <span className="stat-value-large">
                        {formatNumber(financeData.monthlyStats.revenue)}₽
                      </span>
                    </div>
                  </div>

                  <div className="stat-item-large bookings">
                    <div className="stat-content">
                      <span className="stat-label">Подтверждённых записей</span>
                      <span className="stat-value-large">
                        {financeData.monthlyStats.bookings}
                      </span>
                    </div>
                  </div>

                  <div className="stat-item-large average">
                    <div className="stat-content">
                      <span className="stat-label">Средний чек</span>
                      <span className="stat-value-large">
                        {formatNumber(financeData.monthlyStats.averagePrice)}₽
                      </span>
                    </div>
                  </div>

                  <div className="stat-item-large cancelled">
                    <div className="stat-content">
                      <span className="stat-label">Отменённых записей</span>
                      <span className="stat-value-large">
                        {financeData.monthlyStats.cancelledBookings}
                      </span>
                    </div>
                  </div>

                  <div className="stat-item-large refunds">
                    <div className="stat-content">
                      <span className="stat-label">Сумма возвратов</span>
                      <span className="stat-value-large">
                        {formatNumber(financeData.monthlyStats.refunds)}₽
                      </span>
                    </div>
                  </div>

                  <div className="stat-item-large net">
                    <div className="stat-content">
                      <span className="stat-label">Чистый результат</span>
                      <span className="stat-value-large">
                        {formatNumber(netMonthIncome)}₽
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="transactions-section">
              <div className="panel-header panel-header-tight">
                <h3>Операции</h3>
                <span className="panel-caption">
                  Все списания, поступления и возвраты по клубу
                </span>
              </div>

              {financeData.combinedTransactions.length === 0 ? (
                <div className="empty-transactions">
                  <div className="empty-icon" />
                  <h4>Операций ещё нет</h4>
                  <p>После первых платежей данные появятся в этом блоке</p>
                </div>
              ) : (
                <div className="transactions-table">
                  <div className="table-header">
                    <span>Дата</span>
                    <span>Тип</span>
                    <span>Описание</span>
                    <span>Сумма</span>
                  </div>

                  <div className="table-body">
                    {financeData.combinedTransactions.map((transaction) => (
                      <div key={transaction.id} className="table-row">
                        <div className="transaction-date">
                          {new Date(transaction.created_at).toLocaleDateString('ru-RU')}
                          <span className="transaction-time">
                            {new Date(transaction.created_at).toLocaleTimeString('ru-RU', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>

                        <div className="transaction-type">
                          <span
                            className={`type-badge ${getTransactionTypeClass(
                              transaction.type
                            )}`}
                          >
                            {getTransactionTypeLabel(transaction.type)}
                          </span>
                        </div>

                        <div className="transaction-description">
                          {getTransactionDescription(transaction)}
                        </div>

                        <div
                          className={`transaction-amount ${getTransactionTypeClass(
                            transaction.type
                          )}`}
                        >
                          {formatTransactionAmount(transaction)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="info-section">
              <h4>Как формируются показатели</h4>
              <p>
                В расчётах используются только подтверждённые и завершённые бронирования.
                Отменённые записи учитываются как возвраты, что отражается в чистом результате.
                Данные на странице обновляются при каждом обращении к разделу финансов.
              </p>
            </div>
          </div>
        )}
      </div>
    </ClubLayout>
  );
};

export default ClubFinancesPage;
